import {
  IconDefinition,
  faGear,
  faRefresh,
  faTags,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import {
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { useAuth } from '../hooks/auth';
import useCachedResources from '../hooks/cached-resources';
import useIndexes from '../hooks/indexes';
import useJobs from '../hooks/jobs';
import { useLocalStorage } from '../hooks/local-storage';
import { Index, Intent, Job } from '../types';
import classNames from '../utilities/class-names';
import {
  INDEX_REBUILD_STARTED_EVENT,
  announceIndexRebuild,
  describeIndexBuildProgress,
  describeListPurgeProgress,
  isJobRetrying,
  jobSubject,
  listPurgeName,
} from '../utilities/jobs';
import pluralize from '../utilities/pluralize';
import './JobsMenu.scss';

// Poll quickly while something is running, so progress moves, and slowly
// otherwise, to notice jobs queued from elsewhere (e.g. by a token client)
const ACTIVE_POLL_INTERVAL = 3000;
const IDLE_POLL_INTERVAL = 30000;

const ACTIVE_JOBS_LIMIT = 10;

// Failures are looked for among this many of the most recent jobs, which also
// shows whether a failed job has since been superseded by a newer one
const RECENT_JOBS_LIMIT = 25;

// Dismissed failures are remembered in local storage; only the most recent
// ones need to be, since older failures drop out of the recent jobs anyway
const DISMISSED_JOBS_LIMIT = 100;

type JobsState = {
  active: Job[];
  recent: Job[];
};

type JobDisplay = {
  icon: IconDefinition;
  title: string;
  detail: string;
  link?: string;
};

type JobsMenuItemAction = {
  icon: IconDefinition;
  label: string;
  pending: boolean;
  onClick: () => void;
};

type JobsMenuItemProps = {
  display: JobDisplay;
  intent: Intent;
  action?: JobsMenuItemAction;
  onDismiss?: () => void;
};

function JobsMenuItem({
  display,
  intent,
  action,
  onDismiss,
}: JobsMenuItemProps): ReactElement {
  const content = (
    <>
      <FontAwesomeIcon
        className="jobs-menu-item-icon"
        icon={display.icon}
        fixedWidth
      />
      <span className="jobs-menu-item-text">
        <span className="jobs-menu-item-title">{display.title}</span>
        <span className="jobs-menu-item-detail">{display.detail}</span>
      </span>
    </>
  );

  return (
    <li className={classNames('jobs-menu-item', `jobs-menu-item-${intent}`)}>
      {display.link ? (
        <Link className="jobs-menu-item-content" to={display.link}>
          {content}
        </Link>
      ) : (
        <span className="jobs-menu-item-content">{content}</span>
      )}
      {action && (
        <button
          type="button"
          className="jobs-menu-item-action"
          title={action.label}
          aria-label={`${action.label}: ${display.title}`}
          disabled={action.pending}
          onClick={action.onClick}
        >
          <FontAwesomeIcon icon={action.icon} spin={action.pending} />
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          className="jobs-menu-item-dismiss"
          title="Dismiss"
          aria-label={`Dismiss ${display.title}`}
          onClick={onDismiss}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </li>
  );
}

/**
 * Shows the current user's running and failed background jobs
 *
 * The button is only shown when there's something in the menu: it spins while
 * jobs are waiting or running, and has a pip when a job has failed (red) or is
 * being retried after an error (yellow)
 */
export function JobsMenu(): ReactElement {
  const { pathname } = useLocation();
  const { databaseUser: user, loggedIn } = useAuth();
  const { index: indexJobs } = useJobs();
  const {
    rebuild: rebuildIndex,
    error: rebuildError,
    resetError: resetRebuildError,
  } = useIndexes();
  const { list: cachedList, index: cachedIndex } = useCachedResources();
  const tooltipId = `jobs-menu-tooltip-${useId()}`;

  const [jobs, setJobs] = useState<JobsState | null>(null);
  const [open, setOpen] = useState(false);
  const [dismissedJobIds, setDismissedJobIds] = useLocalStorage(
    'dismissed-jobs',
    []
  );

  // Incremented to fetch jobs straight away, e.g. after starting a rebuild
  const [refreshCount, setRefreshCount] = useState(0);

  // The failed build job a rebuild is being started for, and why the last
  // attempt to start one was refused
  const [rebuildingJobId, setRebuildingJobId] = useState<string | null>(null);
  const rebuildTargetRef = useRef<string | null>(null);
  const [rebuildFailure, setRebuildFailure] = useState<{
    jobId: string;
    message: string;
  } | null>(null);

  const userId = user?.id;
  const admin = !!user?.admin;

  // Admins can see everyone's jobs, but this menu is only about their own
  const fetchJobs = useCallback(
    async (signal: AbortSignal): Promise<JobsState | null> => {
      const ownJobs = admin && userId ? { userId } : {};
      const [active, recent] = await Promise.all([
        indexJobs(
          {
            ...ownJobs,
            status: ['queued', 'running'],
            page: 1,
            limit: ACTIVE_JOBS_LIMIT,
            order: 'runAt',
            direction: 'asc',
          },
          signal
        ),
        indexJobs(
          {
            ...ownJobs,
            page: 1,
            limit: RECENT_JOBS_LIMIT,
            order: 'createdAt',
            direction: 'desc',
          },
          signal
        ),
      ]);

      if (!active || !recent) {
        return null;
      }

      return { active: active.data, recent: recent.data };
    },
    [indexJobs, admin, userId]
  );

  // Poll for jobs, and fetch them straight away after navigating, since that's
  // usually when a job has just been queued (e.g. after creating an index).
  // Polling pauses while the page is hidden
  const busyRef = useRef(false);
  useEffect(() => {
    if (!loggedIn || !userId) {
      setJobs(null);
      return;
    }

    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let fetching = false;

    const poll = async () => {
      clearTimeout(timeout);

      if (fetching || document.hidden) {
        return;
      }

      fetching = true;
      const result = await fetchJobs(controller.signal);
      fetching = false;

      if (controller.signal.aborted) {
        return;
      }

      if (result) {
        busyRef.current = result.active.length > 0;
        setJobs(result);
      }

      timeout = setTimeout(
        poll,
        busyRef.current ? ACTIVE_POLL_INTERVAL : IDLE_POLL_INTERVAL
      );
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        poll();
      }
    };

    poll();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      controller.abort();
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loggedIn, userId, fetchJobs, pathname, refreshCount]);

  // A rebuild started anywhere in the dashboard queues a job that supersedes
  // the failure, so fetch jobs rather than wait for the next poll
  useEffect(() => {
    const handleRebuildStarted = () => setRefreshCount(count => count + 1);

    window.addEventListener(INDEX_REBUILD_STARTED_EVENT, handleRebuildStarted);

    return () =>
      window.removeEventListener(
        INDEX_REBUILD_STARTED_EVENT,
        handleRebuildStarted
      );
  }, []);

  useEffect(() => {
    if (rebuildError && rebuildTargetRef.current) {
      setRebuildFailure({
        jobId: rebuildTargetRef.current,
        message: rebuildError.message,
      });
    }
  }, [rebuildError]);

  const rebuild = useCallback(
    async (job: Job) => {
      if (!job.listId || !job.indexId) {
        return;
      }

      rebuildTargetRef.current = job.id;
      setRebuildingJobId(job.id);
      setRebuildFailure(null);
      resetRebuildError();

      const rebuilt: Index | void = await rebuildIndex(job.listId, job.indexId);

      if (rebuilt) {
        announceIndexRebuild(rebuilt);
      }

      setRebuildingJobId(null);
    },
    [rebuildIndex, resetRebuildError]
  );

  const active = useMemo(() => jobs?.active ?? [], [jobs]);

  // A failure is only worth showing until it's dismissed, or until a newer job
  // for the same resource has been queued (e.g. the index build was retried)
  const failed = useMemo(() => {
    if (!jobs) {
      return [];
    }

    const dismissed = new Set<string>(
      Array.isArray(dismissedJobIds) ? dismissedJobIds : []
    );
    const all = [...jobs.active, ...jobs.recent];

    return jobs.recent.filter(
      job =>
        job.status === 'failed' &&
        !dismissed.has(job.id) &&
        !all.some(
          other =>
            other.id !== job.id &&
            jobSubject(other) === jobSubject(job) &&
            dayjs(other.createdAt).isAfter(job.createdAt)
        )
    );
  }, [jobs, dismissedJobIds]);

  const retrying = active.filter(isJobRetrying);
  const pipIntent: Intent | null =
    failed.length > 0 ? 'danger' : retrying.length > 0 ? 'warning' : null;

  const hasJobs = active.length > 0 || failed.length > 0;

  const dismiss = useCallback(
    (jobIds: string[]) => {
      const previous: string[] = Array.isArray(dismissedJobIds)
        ? dismissedJobIds
        : [];

      setDismissedJobIds(
        [...previous.filter(id => !jobIds.includes(id)), ...jobIds].slice(
          -DISMISSED_JOBS_LIMIT
        )
      );
    },
    [dismissedJobIds, setDismissedJobIds]
  );

  const describeJob = (job: Job): JobDisplay => {
    switch (job.type) {
      case 'index.build': {
        const indexName = job.indexId ? cachedIndex(job.indexId)?.name : null;
        const listName = job.listId ? cachedList(job.listId)?.name : null;
        const name = indexName ? `index "${indexName}"` : 'index';
        const link =
          job.listId && job.indexId
            ? `/lists/${job.listId}/indexes/${job.indexId}`
            : undefined;
        const inList = listName ? ` in ${listName}` : '';

        if (job.status === 'failed') {
          return {
            icon: faTags,
            title: `Failed to build ${name}${inList}`,
            detail:
              rebuildFailure?.jobId === job.id
                ? `Couldn't start a rebuild: ${rebuildFailure.message}`
                : (job.error?.message ?? 'The build failed'),
            link,
          };
        }

        return {
          icon: faTags,
          title: `Building ${name}${inList}`,
          detail: isJobRetrying(job)
            ? `Retrying (attempt ${
                job.status === 'running' ? job.attempts : job.attempts + 1
              } of ${job.maxAttempts}): ${
                job.error?.message ?? 'the last attempt failed'
              }`
            : describeIndexBuildProgress(job),
          link,
        };
      }

      case 'list.purge': {
        // The list itself is already gone, so its name comes from the job (or
        // from whatever the dashboard saw of it before it was deleted), and
        // there is nothing to link to
        const name =
          listPurgeName(job) ??
          (job.listId ? cachedList(job.listId)?.name : null);
        const list = name ? `list "${name}"` : 'a deleted list';

        if (job.status === 'failed') {
          return {
            icon: faTrash,
            title: `Failed to delete the contents of ${list}`,
            detail: job.error?.message ?? 'The deletion failed',
          };
        }

        return {
          icon: faTrash,
          title: `Deleting the contents of ${list}`,
          detail: isJobRetrying(job)
            ? `Retrying (attempt ${
                job.status === 'running' ? job.attempts : job.attempts + 1
              } of ${job.maxAttempts}): ${
                job.error?.message ?? 'the last attempt failed'
              }`
            : describeListPurgeProgress(job),
        };
      }

      default:
        return {
          icon: faGear,
          title: job.type,
          detail:
            job.status === 'failed'
              ? (job.error?.message ?? 'The job failed')
              : job.status === 'queued'
                ? 'Waiting to start…'
                : 'Running…',
        };
    }
  };

  // Keep the button while the menu is open, so it doesn't vanish from under
  // the pointer when the last job finishes
  const visible = loggedIn && (hasJobs || open);

  const label = [
    active.length > 0 &&
      `${active.length} ${pluralize('job', active.length)} running`,
    failed.length > 0 &&
      `${failed.length} ${pluralize('job', failed.length)} failed`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className={classNames('jobs-menu', visible ? 'jobs-menu-visible' : '')}
    >
      {visible && (
        <>
          <button
            type="button"
            className="jobs-menu-button"
            data-tooltip-id={tooltipId}
            data-tooltip-place="bottom-end"
            aria-label={label || 'Background jobs'}
          >
            <FontAwesomeIcon icon={faGear} spin={active.length > 0} />
            {pipIntent && (
              <span
                className={classNames(
                  'jobs-menu-pip',
                  `jobs-menu-pip-${pipIntent}`
                )}
              />
            )}
          </button>
          {createPortal(
            <Tooltip
              id={tooltipId}
              clickable
              className="jobs-menu-tooltip"
              afterShow={() => setOpen(true)}
              afterHide={() => setOpen(false)}
            >
              <div className="jobs-menu-heading">
                <span>Background jobs</span>
                {failed.length > 1 && (
                  <button
                    type="button"
                    className="jobs-menu-dismiss-all"
                    onClick={() => dismiss(failed.map(job => job.id))}
                  >
                    Dismiss all failures
                  </button>
                )}
              </div>
              {hasJobs ? (
                <ul className="jobs-menu-items">
                  {failed.map(job => (
                    <JobsMenuItem
                      key={job.id}
                      display={describeJob(job)}
                      intent="danger"
                      action={
                        job.type === 'index.build' && job.listId && job.indexId
                          ? {
                              icon: faRefresh,
                              label: 'Rebuild',
                              pending: rebuildingJobId === job.id,
                              onClick: () => rebuild(job),
                            }
                          : undefined
                      }
                      onDismiss={() => dismiss([job.id])}
                    />
                  ))}
                  {active.map(job => (
                    <JobsMenuItem
                      key={job.id}
                      display={describeJob(job)}
                      intent={isJobRetrying(job) ? 'warning' : 'info'}
                    />
                  ))}
                </ul>
              ) : (
                <p className="jobs-menu-empty">All jobs have finished.</p>
              )}
            </Tooltip>,
            document.body
          )}
        </>
      )}
    </div>
  );
}
